import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import {
  links as linksApi,
  domains as domainsApi,
  ApiError,
  type Link,
  type Domain,
} from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { QRCode } from '@/components/QRCode';
import { Button, ButtonLink, Surface } from '@/components/ui';

const FREE_HOST = 'go.poof.bio';

export default function NewLink() {
  const _navigate = useNavigate();
  const [targetUrl, setTargetUrl] = useState('');
  const [title, setTitle] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [domainId, setDomainId] = useState<string>(''); // '' = free subdomain
  const [subdomain, setSubdomain] = useState('');
  const [domainOptions, setDomainOptions] = useState<Domain[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Link | null>(null);

  useEffect(() => {
    domainsApi
      .list()
      .then((r) => setDomainOptions(r.domains.filter((d) => d.sslStatus === 'active')))
      .catch(() => {});
  }, []);
  void _navigate;

  const activeDomain = domainId === '' ? null : domainOptions.find((d) => d.id === domainId);
  const baseHost = activeDomain?.hostname || FREE_HOST;
  const wildcardAvailable = !!activeDomain?.wildcardEnabled;
  const effectiveHost = wildcardAvailable && subdomain ? `${subdomain}.${baseHost}` : baseHost;

  // Reset the subdomain when switching to a domain that doesn't support
  // wildcards — otherwise it'd silently get rejected at the API.
  useEffect(() => {
    if (!wildcardAvailable) setSubdomain('');
  }, [wildcardAvailable]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const link = await linksApi.create({
        targetUrl,
        title: title || undefined,
        shortCode: shortCode || undefined,
        domainId: domainId || null,
        subdomain: subdomain || undefined,
      });
      setCreated(link);
    } catch (e) {
      const msg =
        e instanceof ApiError ? errorMessageFor(e.code) : 'Something went wrong. Try again.';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    // When the customer left the destination URL blank, the server
    // stored a placeholder (poof.bio). Surface that honestly + nudge
    // them to configure the link rather than implying it's "live"
    // pointing at our marketing site.
    const isPlaceholderTarget = created.targetUrl === 'https://poof.bio';
    return (
      <DashboardLayout title={isPlaceholderTarget ? 'Link reserved' : 'Your link is live'}>
        <div className="mx-auto max-w-xl">
          <p className="text-[var(--color-on-surface-variant)]">
            {isPlaceholderTarget
              ? 'Now configure where it should send visitors — attach a landing page or set a destination URL.'
              : "Drop it in your Instagram bio. When someone taps it, they'll be sent straight to Safari (no in-app browser)."}
          </p>

          <Surface tone="raised" className="mt-7 p-7">
            <div className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
              Your short link
            </div>
            <div className="mt-2 break-all font-mono text-lg font-semibold text-[var(--color-foreground)]">
              {created.fullUrl}
            </div>
            {!isPlaceholderTarget && (
              <div className="mt-1.5 truncate font-mono text-xs text-[var(--color-on-surface-variant)]">
                → {created.targetUrl}
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-2">
              <CopyButton text={created.fullUrl} />
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  window.open(created.fullUrl, '_blank', 'noopener,noreferrer')
                }
              >
                Test in browser ↗
              </Button>
            </div>
          </Surface>

          {!isPlaceholderTarget && (
            <Surface tone="raised" className="mt-5 flex flex-col items-center gap-4 p-7">
              <div className="inline-block rounded-2xl bg-white p-4">
                <QRCode value={created.fullUrl} size={180} />
              </div>
              <p className="max-w-sm text-center text-xs text-[var(--color-on-surface-variant)]">
                Scan from your phone, then post that link to your IG bio + click
                it from inside Instagram. Should escape to Safari instantly.
              </p>
            </Surface>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <ButtonLink
              to={isPlaceholderTarget ? `/app/links/${created.id}` : '/app'}
              variant={isPlaceholderTarget ? 'primary' : 'ghost'}
            >
              {isPlaceholderTarget ? 'Configure this link →' : 'Back to overview'}
            </ButtonLink>
            <Button
              variant={isPlaceholderTarget ? 'ghost' : 'primary'}
              onClick={() => {
                setCreated(null);
                setTargetUrl('');
                setTitle('');
                setShortCode('');
              }}
            >
              Create another
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="New link">
      <div className="mx-auto max-w-xl">
        <p className="text-[var(--color-on-surface-variant)]">
          We&apos;ll create a short{' '}
          <code className="mx-1 rounded-md bg-[var(--color-surface-container)] px-1.5 py-0.5 font-mono text-xs">
            go.poof.bio/&hellip;
          </code>{' '}
          link that escapes the in-app browser when tapped from Instagram.
          Point it at a URL, or leave the destination blank and attach a
          landing page after.
        </p>

        <Surface tone="raised" className="mt-7 p-7">
          <form onSubmit={onSubmit} className="flex flex-col gap-5">
            <Field
              label="Destination URL"
              optional
              hint="Where visitors should go when they tap this link. Leave blank if you plan to attach a landing page after creating — you can change this any time."
            >
              <input
                type="text"
                value={targetUrl}
                onChange={(e) => setTargetUrl(e.target.value)}
                autoFocus
                className={INPUT_CLASS}
              />
            </Field>

            <Field
              label="Title"
              optional
              hint="For your reference. Visitors never see it."
            >
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>

            {domainOptions.length > 0 && (
              <Field label="Domain">
                <select
                  value={domainId}
                  onChange={(e) => setDomainId(e.target.value)}
                  className={INPUT_CLASS}
                >
                  <option value="">{FREE_HOST} (free subdomain)</option>
                  {domainOptions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.hostname}
                      {d.wildcardEnabled ? '  (+ any subdomain)' : ''}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {wildcardAvailable && (
              <Field
                label="Subdomain"
                optional
                hint={
                  subdomain
                    ? `Link will live at https://${subdomain}.${baseHost}/${shortCode || '…'}`
                    : `Leave blank for the apex (${baseHost}). Or pick a label — visitors will tap a link like ${'<label>'}.${baseHost}.`
                }
              >
                <div className="flex items-stretch overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] focus-within:border-[var(--color-ring)]">
                  <input
                    type="text"
                    value={subdomain}
                    onChange={(e) =>
                      setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                    }
                    placeholder="e.g. promo, bio, page1"
                    pattern="[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?"
                    maxLength={63}
                    className="flex-1 bg-transparent px-4 py-3 font-mono text-sm outline-none"
                  />
                  <span className="flex items-center px-4 font-mono text-sm text-[var(--color-on-surface-variant)]">
                    .{baseHost}
                  </span>
                </div>
              </Field>
            )}

            <Field
              label="Custom short code"
              optional
              hint="Leave blank to get a random 6-character code. Lowercase letters, digits, and hyphens only."
            >
              <div className="flex items-stretch overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] focus-within:border-[var(--color-ring)]">
                <span className="flex items-center px-4 font-mono text-sm text-[var(--color-on-surface-variant)]">
                  {effectiveHost}/
                </span>
                <input
                  type="text"
                  value={shortCode}
                  onChange={(e) => setShortCode(e.target.value.toLowerCase())}
                  pattern="[a-z0-9][a-z0-9\-]*[a-z0-9]"
                  minLength={3}
                  maxLength={32}
                  className="flex-1 bg-transparent px-2 py-3 font-mono text-sm outline-none"
                />
              </div>
            </Field>

            {err && <p className="text-sm text-[var(--color-destructive)]">{err}</p>}

            <Button type="submit" size="lg" disabled={busy} fullWidth>
              {busy ? 'Creating…' : 'Create link →'}
            </Button>
          </form>
        </Surface>
      </div>
    </DashboardLayout>
  );
}

const INPUT_CLASS =
  'rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-4 py-3 text-[var(--color-foreground)] outline-none transition focus:border-[var(--color-ring)]';

function Field({
  label,
  optional,
  hint,
  children,
}: {
  label: string;
  optional?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
        {label}
        {optional && <span className="ml-1.5 opacity-60">(optional)</span>}
      </span>
      {children}
      {hint && (
        <span className="text-xs text-[var(--color-on-surface-variant)]">
          {hint}
        </span>
      )}
    </label>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="primary"
      size="sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // ignore
        }
      }}
    >
      {copied ? '✓ Copied' : 'Copy link'}
    </Button>
  );
}

function errorMessageFor(code: string): string {
  switch (code) {
    case 'target_url_required':
      return 'A destination URL is required.';
    case 'target_url_invalid':
      return "That URL doesn't look right. Try something like instagram.com/yourname.";
    case 'short_code_invalid':
      return 'Custom codes must be lowercase letters, digits, and hyphens (3-32 chars).';
    case 'short_code_taken':
      return 'That short code is already taken on this domain. Try another.';
    case 'domain_not_yours':
      return "That domain doesn't belong to your workspace.";
    case 'domain_not_ready':
      return "That domain isn't fully provisioned yet. Wait for SSL + DNS verification, then try again.";
    case 'subdomain_invalid':
      return 'Subdomain labels are lowercase letters, digits, and hyphens (1–63 chars, no leading/trailing hyphen).';
    case 'subdomain_requires_wildcard':
      return 'This domain doesn’t have wildcard subdomains enabled. Enable it on the domain settings, or pick a different domain.';
    case 'subdomain_requires_custom_domain':
      return 'Subdomain labels only work on your own custom domain — not the shared free subdomain.';
    default:
      return code;
  }
}
